import React from 'react';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaFacebookF, FaTwitter, FaLinkedinIn, FaInstagram, FaArrowRight } from 'react-icons/fa';

const Footer = () => {
  const services = [
    'Droit des Sociétés',
    'Expertise Corporative',
    'Droit Financier',
    'Contentieux Civil',
    'Droit Minier',
    'Droit des Assurances', 
    'Responsabilité sociale (RSE)',
    'Environnement et Hydrocarbures'
  ];

  // Les couleurs spécifiques des réseaux sociaux sont remplacées par la couleur d'accentuation (Beige/Or)
  const socialLinks = [
    { icon: <FaLinkedinIn />, label: 'LinkedIn', url: '#' },
    { icon: <FaTwitter />, label: 'Twitter', url: '#' },
    { icon: <FaFacebookF />, label: 'Facebook', url: '#' },
    { icon: <FaInstagram />, label: 'Instagram', url: '#' },
  ];

  return (
    <footer className="footer">
      {/* Texture de fond luxueuse */}
      <div className="noise-overlay" />

      {/* Section Principale */}
      <div className="main-footer">
        <div className="footer-grid">
          
          {/* Colonne Logo & Description */}
          <div className="footer-column brand-column">
            <div className="logo-section">
              <h2 className="footer-logo">
                KMJ <span className="logo-highlight">LAWFIRM</span>
              </h2>
              <p className="company-description">
                Cabinet d'avocats spécialisé en droit des affaires. 
                Nous offrons des solutions juridiques innovantes, rigoureuses et personnalisées 
                pour sécuriser vos opérations depuis plus de 7 ans.
              </p>
              <div className="trust-badges">
                <div className="badge">Rigueur & Excellence</div>
                <div className="badge">Confidentialité Absolue</div>
              </div>
            </div>
          </div>

          {/* Colonne Contact */}
          <div className="footer-column">
            <h3 className="column-title">
              Contact
              <span className="title-decoration" />
            </h3>
            <div className="contact-list">
              <div className="contact-item">
                <FaMapMarkerAlt className="contact-icon" />
                <div className="contact-text">
                  <h4 className="contact-title">Siège Social</h4>
                  <p className="contact-detail">
                    03, Imm ITC, Avenue Bas-Congo<br />
                    Gare centrale, Gombe <br />
                    Kinshasa, RDC
                  </p>
                </div>
              </div>
              
              <div className="contact-item">
                <FaPhone className="contact-icon" />
                <div className="contact-text">
                  <h4 className="contact-title">Téléphone</h4>
                  <p className="contact-detail">
                   +243 822 764 820<br />
                   +243 820 676 299
                  </p>
                </div>
              </div>
              
              <div className="contact-item">
                <FaEnvelope className="contact-icon" />
                <div className="contact-text">
                  <h4 className="contact-title">Email</h4>
                  <p className="contact-detail">
                    <a href="mailto:contact@kmj-lawfirm.com">contact@kmj-lawfirm.com</a><br />
                    <a href="mailto:infos@kmj-lawfirm.com">infos@kmj-lawfirm.com</a>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Colonne Services */}
          <div className="footer-column">
            <h3 className="column-title">
              Expertises
              <span className="title-decoration" />
            </h3>
            <div className="services-grid">
              {services.map((service, index) => (
                <motion.a
                  key={index}
                  href="#"
                  className="service-item"
                  whileHover={{ x: 5 }}
                  transition={{ duration: 0.2 }}
                >
                  <span className="service-bullet" />
                  <span className="service-text">{service}</span>
                </motion.a>
              ))}
            </div>
          </div>

          {/* Colonne Newsletter & Social */}
          <div className="footer-column">
            <h3 className="column-title">
              Restez Informés
              <span className="title-decoration" />
            </h3>
            <div className="newsletter-section">
              <p className="newsletter-description">
                Inscrivez-vous à notre lettre d'information pour recevoir 
                les dernières actualités et veilles juridiques.
              </p>
              
              <form className="newsletter-form">
                <input 
                  type="email" 
                  placeholder="Votre adresse email professionnelle" 
                  className="newsletter-input"
                  required
                />
                <motion.button 
                  type="submit" 
                  className="newsletter-button"
                  whileHover={{ backgroundColor: '#C1A68D', color: '#1A1A1A' }}
                  whileTap={{ scale: 0.98 }}
                >
                  S'abonner
                </motion.button>
              </form>
              
              <div className="social-section">
                <div className="social-links">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.url}
                      className="social-link"
                      whileHover={{ y: -3, backgroundColor: 'rgba(193, 166, 141, 0.1)', borderColor: '#C1A68D' }}
                      aria-label={social.label}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Barre de séparation */}
      <div className="divider" />

      {/* Copyright */}
      <div className="copyright-section">
        <div className="copyright-content">
          <p className="copyright-text">
            © {new Date().getFullYear()} CABINET KMJ-LAWFIRM. Tous droits réservés.
          </p>
          <div className="legal-links">
            <a href="#" className="legal-link">Mentions légales</a>
            <span className="link-separator">|</span>
            <a href="#" className="legal-link">Politique de confidentialité</a>
            <span className="link-separator">|</span>
            <a href="#" className="legal-link">Conditions d'utilisation</a>
          </div>
        </div>
      </div>

      <style jsx>{`
        .footer {
          position: relative;
          background: #0A0A0A;
          color: #F5F0E6;
          border-top: 1px solid rgba(193, 166, 141, 0.15);
          font-family: system-ui, -apple-system, sans-serif;
        }

        .noise-overlay {
          position: absolute;
          inset: 0;
          background-image: url('https://www.transparenttextures.com/patterns/stardust.png');
          opacity: 0.15;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 1;
        }

        /* Section Principale */
        .main-footer {
          position: relative;
          z-index: 2;
          padding: 6rem 2rem 4rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .footer-grid {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
          gap: 4rem;
        }

        @media (max-width: 1024px) {
          .footer-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 3rem;
          }
        }

        @media (max-width: 768px) {
          .footer-grid {
            grid-template-columns: 1fr;
            gap: 3rem;
          }
        }

        /* Logo Section */
        .brand-column {
          padding-right: 2rem;
        }

        .logo-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .footer-logo {
          font-size: 2rem;
          font-weight: 700;
          margin: 0;
          color: #F5F0E6;
          letter-spacing: 2px;
        }

        .logo-highlight {
          color: #C1A68D;
        }

        .company-description {
          font-size: 0.95rem;
          line-height: 1.7;
          color: #D4C5B0;
          margin: 0;
          font-weight: 300;
        }

        .trust-badges {
          display: flex;
          gap: 0.8rem;
          flex-wrap: wrap;
          margin-top: 0.5rem;
        }

        .badge {
          border: 1px solid rgba(193, 166, 141, 0.3);
          padding: 0.5rem 1rem;
          border-radius: 2px;
          font-size: 0.75rem;
          color: #C1A68D;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-weight: 500;
          background: rgba(193, 166, 141, 0.05);
        }

        /* Titres de colonnes */
        .column-title {
          position: relative;
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 2rem;
          padding-bottom: 0.8rem;
          color: #F5F0E6;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .title-decoration {
          position: absolute;
          bottom: 0;
          left: 0;
          width: 30px;
          height: 1px;
          background: #C1A68D;
        }

        /* Liste de contacts */
        .contact-list {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .contact-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
        }

        .contact-icon {
          color: #C1A68D;
          font-size: 1.1rem;
          margin-top: 0.2rem;
        }

        .contact-text {
          flex: 1;
        }

        .contact-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: #F5F0E6;
          margin: 0 0 0.3rem 0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .contact-detail {
          font-size: 0.9rem;
          line-height: 1.6;
          color: #D4C5B0;
          margin: 0;
          font-weight: 300;
        }

        .contact-detail a {
          color: #D4C5B0;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .contact-detail a:hover {
          color: #C1A68D;
        }

        /* Services */
        .services-grid {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .service-item {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          text-decoration: none;
          color: #D4C5B0;
          transition: color 0.3s ease;
        }

        .service-item:hover {
          color: #C1A68D;
        }

        .service-item:hover .service-bullet {
          background: #C1A68D;
        }

        .service-bullet {
          width: 4px;
          height: 4px;
          background: rgba(193, 166, 141, 0.4);
          border-radius: 1px;
          transition: background 0.3s ease;
        }

        .service-text {
          font-size: 0.9rem;
          font-weight: 300;
        }

        /* Newsletter */
        .newsletter-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .newsletter-description {
          font-size: 0.9rem;
          line-height: 1.6;
          color: #D4C5B0;
          margin: 0;
          font-weight: 300;
        }

        .newsletter-form {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
        }

        .newsletter-input {
          width: 100%;
          padding: 0.8rem 1rem;
          background: rgba(26, 26, 26, 0.8);
          border: 1px solid rgba(193, 166, 141, 0.2);
          border-radius: 2px;
          color: #F5F0E6;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }

        .newsletter-input:focus {
          outline: none;
          border-color: #C1A68D;
          background: rgba(26, 26, 26, 1);
        }

        .newsletter-input::placeholder {
          color: rgba(212, 197, 176, 0.5);
          font-weight: 300;
        }

        .newsletter-button {
          padding: 0.8rem;
          background: transparent;
          color: #C1A68D;
          border: 1px solid #C1A68D;
          border-radius: 2px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          text-transform: uppercase;
          letter-spacing: 1px;
          transition: all 0.3s ease;
        }

        /* Social Links */
        .social-section {
          margin-top: 1rem;
        }

        .social-links {
          display: flex;
          gap: 0.8rem;
        }

        .social-link {
          width: 36px;
          height: 36px;
          background: transparent;
          border: 1px solid rgba(193, 166, 141, 0.3);
          border-radius: 2px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #C1A68D;
          text-decoration: none;
          transition: all 0.3s ease;
          font-size: 1rem;
        }

        /* Barre de séparation */
        .divider {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          height: 1px;
          background: rgba(193, 166, 141, 0.1);
          position: relative;
          z-index: 2;
        }

        /* Copyright */
        .copyright-section {
          position: relative;
          z-index: 2;
          padding: 2rem;
          background: #050505;
        }

        .copyright-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          text-align: center;
        }

        @media (min-width: 768px) {
          .copyright-content {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            text-align: left;
          }
        }

        .copyright-text {
          font-size: 0.85rem;
          color: #D4C5B0;
          margin: 0;
          font-weight: 300;
        }

        .legal-links {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
        }

        .legal-link {
          color: #D4C5B0;
          font-size: 0.85rem;
          text-decoration: none;
          transition: color 0.3s ease;
          font-weight: 300;
        }

        .legal-link:hover {
          color: #C1A68D;
        }

        .link-separator {
          color: rgba(193, 166, 141, 0.3);
          font-size: 0.8rem;
        }

        /* Responsive Adjustments */
        @media (max-width: 768px) {
          .main-footer {
            padding: 4rem 1.5rem 3rem;
          }
          .brand-column {
            padding-right: 0;
          }
          .footer-logo {
            font-size: 1.8rem;
          }
          .newsletter-button {
            padding: 0.9rem;
          }
          .legal-links {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;